import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { featuredServices } from '@/mocks/services';

export default function ServicesSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.reveal-item');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4 tracking-wider uppercase">
            Notre expertise
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-800 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto">
          {featuredServices.map((service) => (
            <div
              key={service.id}
              className="reveal-item opacity-0 translate-y-6 transition-all duration-700 group"
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-300/70 transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-xl h-full flex flex-col">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-3 py-1.5 text-xs font-medium bg-background-50/90 backdrop-blur-sm text-foreground-700 rounded-full border border-background-200/50">
                      {service.category}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary-500 text-background-50 rounded-full">
                      {service.duration} min
                    </span>
                  </div>
                </div>

                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <h3 className="font-heading text-xl text-foreground-800 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1 line-clamp-3">
                    {service.description}
                  </p>

                  <div className="space-y-1.5 mb-4">
                    {service.benefits.slice(0, 2).map((benefit, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-xs text-secondary-500" />
                        </div>
                        <span className="text-xs text-foreground-500">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-background-200/50 mt-auto">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-heading font-bold text-foreground-900">
                        {service.price.toLocaleString('fr-FR')}
                      </span>
                      <span className="text-sm font-semibold text-foreground-700">FCFA</span>
                    </div>
                    <Link
                      to="/rdv"
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
                    >
                      Réserver
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 md:mt-16">
          <Link
            to="/soins"
            className="inline-flex items-center justify-center px-8 py-3.5 rounded-full text-base font-medium border-2 border-primary-500 text-primary-700 hover:bg-primary-500 hover:text-background-50 transition-all duration-300 whitespace-nowrap"
          >
            {t('services.all')}
            <div className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}